// Libs
import classNames from 'classnames/bind';
import { Box, Button, IconButton, Popover, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { BaseAvatarGroup } from '~/components/common';
// Others
import { ISchedule } from '~/utils/interface/schedule';
import { EMPTY_STRING } from '~/utils/constants/common';
import { DateFormatEnum } from '~/utils/enum/common';
import { formatDate, formatTitleCase } from '~/utils/helper/common';
// Styles, images, icons
import styles from './ScheduleDetail.module.scss';
import { icons } from '~/assets';

type Props = {
  id?: string;
  isOpen: boolean;
  anchorEl?: HTMLDivElement | null;
  data?: ISchedule;
  onClose: () => void;
  onShowConfirmDelete?: (scheduleId: string) => void;
  onShowEditSchedule?: (scheduleId: string) => void;
};

const cx = classNames.bind(styles);

const ScheduleDetail = (props: Props) => {
  //#region Destructuring Props
  const { id, isOpen, data, onClose, onShowConfirmDelete, onShowEditSchedule, anchorEl } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/schedule']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowEditSchedule = () => {
    if (!data?.id) return;

    onShowEditSchedule && onShowEditSchedule(data?.id);
  };

  const handleShowConfirmDelete = () => {
    if (!data?.id) return;

    onShowConfirmDelete && onShowConfirmDelete(data?.id);
  };
  //#endregion Handle Function

  return (
    <Popover
      id={id}
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          className: cx('paper'),
        },
      }}
    >
      <Stack gap={'16px'}>
        <Stack direction={'row'} alignItems={'center'}>
          <Typography component='span' className={cx('key')}>
            {t('schedule_detail_item_label_type')}
          </Typography>

          <Typography component='span' className={cx('value')}>
            {data?.businessType ? formatTitleCase(data?.businessType) : EMPTY_STRING}
          </Typography>
        </Stack>

        <Stack direction={'row'} alignItems={'center'}>
          <Typography component='span' className={cx('key')}>
            {t('schedule_detail_item_label_name')}
          </Typography>

          <Typography component='span' className={cx('value')}>
            {data?.eventName || EMPTY_STRING}
          </Typography>
        </Stack>

        <Stack direction={'row'} alignItems={'center'}>
          <Typography component='span' className={cx('key')}>
            {t('schedule_detail_item_label_start_date')}
          </Typography>

          <Typography component='span' className={cx('value')}>
            {formatDate(data?.startDate, DateFormatEnum.DD_MMM_YYYY)}
          </Typography>
        </Stack>

        <Stack direction={'row'} alignItems={'center'}>
          <Typography component='span' className={cx('key')}>
            {t('schedule_detail_item_label_end_date')}
          </Typography>

          <Typography component='span' className={cx('value')}>
            {formatDate(data?.endDate, DateFormatEnum.DD_MMM_YYYY)}
          </Typography>
        </Stack>

        <Stack direction={'row'} alignItems={'center'}>
          <Typography component='span' className={cx('key')}>
            {t('schedule_detail_item_label_assignee')}
          </Typography>

          <BaseAvatarGroup avatars={data?.assignees || []} borderColor='#FFF' max={5} />
        </Stack>

        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Box component={'div'} sx={{ width: '120px' }}>
            <IconButton color='error' onClick={handleShowConfirmDelete}>
              <img src={icons.activityIconDelete} alt={t('common_alt_img')} className={cx('deleteIcon')} />
            </IconButton>
          </Box>

          <Stack direction={'row'} alignItems={'center'} gap={'8px'}>
            <Button variant='contained' onClick={handleShowEditSchedule}>
              {t('schedule_detail_item_btn_edit')}
            </Button>

            <Button variant='contained' onClick={handleShowEditSchedule}>
              {t('schedule_detail_item_btn_view_detail')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Popover>
  );
};

export default ScheduleDetail;
